import { LockFilled } from '@ant-design/icons'
import type { HomeDto } from '@lmc/ts-common'
import { Form, FormCard, FormItem, PageHeader } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import * as GlobalActions from 'actions/Global'
import { Form as AntdForm, Switch } from 'antd'
import { postJSON } from 'common/services/api'
import { showToast } from 'common/utils'
import NavigationModal from 'components/NavigationModal/NavigationModal'
import useHomeType from 'hooks/useHomeType'
import useMixpanelEvent, { MixpanelEvents } from 'hooks/useMixpanelEvent'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from 'react-query'
import { useDispatch, useSelector } from 'react-redux'
import { handleSubmit } from 'screens/Rostering/formComponents/utils'
import FixedFooter from 'screens/Rostering/siteSettings/components/FixedFooter'
import styled from 'styled-components'
import { AccessRestriction } from '../../../components/generic'
import { NAMESPACE } from '../../../locale'
import { familyPortalSettingsConstants } from '../../../locale/familyPortalSettings'
import Operations from '../../../operations'
import { FamilyLogCategoryItemsField } from './components/FamilyLogCategoryItemsField'
import logAllDetailsImg from './images/log all details.png'
import logLoggedByOnlyImg from './images/log logged by only.png'
import logNoDetailsImg from './images/log no details.png'
import logTimeOnlyImg from './images/log time only.png'
import rotaVisitOnlyImg from './images/visit details.png'
import rotaVisitLastUpdatedImg from './images/visit details and last udpated details.png'
import rotaVisitTeamImg from './images/visit details team member allocated.png'
import rotaVisitTeamClockImg from './images/visit details team member allocated and clocking details.png'
import rotaVisitTeamClockLastUpdatedImg from './images/visit details team member allocated and clocking details and last updated details.png'
import rotaVisitTeamLastUpdatedImg from './images/visit details team member allocated and last updated details.png'

type ExtendedHomeDto = HomeDto & {
  familyRostering?: boolean
  familyRotaShowVisitInformation?: boolean
  familyRotaShowTeamMembers?: boolean
  familyRotaShowClockInOut?: boolean
  familyRotaShowLastUpdatedBy?: boolean
}

type FamilySettingsFormValues = {
  familyDocuments: boolean
  familyCarePlans: boolean
  familyMedications: boolean
  familyRostering: boolean
  familyShowDates: boolean
  familyShowNames: boolean
  familyLogCategoryItems: string[]
  familyRotaShowVisitInformation: boolean
  familyRotaShowTeamMembers: boolean
  familyRotaShowClockInOut: boolean
  familyRotaShowLastUpdatedBy: boolean
}

const HELP_ARTICLE_URL =
  'https://support.logmycare.co.uk/en/articles/4248185-how-can-i-customise-the-family-portal'

const getLogDetailsImageUrl = (
  showDates: boolean,
  showNames: boolean
): string => {
  if (showNames) {
    return showDates ? logAllDetailsImg : logLoggedByOnlyImg
  }
  return showDates ? logTimeOnlyImg : logNoDetailsImg
}

const getRotaDetailsImageUrl = (
  showVisitInformation: boolean,
  showTeamMembers: boolean,
  showClockInOut: boolean,
  showLastUpdatedBy: boolean
): string => {
  if (!showVisitInformation) {
    return rotaVisitOnlyImg
  }
  const clockEffective = showTeamMembers && showClockInOut
  if (!showTeamMembers) {
    return showLastUpdatedBy ? rotaVisitLastUpdatedImg : rotaVisitOnlyImg
  }
  if (clockEffective && showLastUpdatedBy) {
    return rotaVisitTeamClockLastUpdatedImg
  }
  if (clockEffective) {
    return rotaVisitTeamClockImg
  }
  if (showLastUpdatedBy) {
    return rotaVisitTeamLastUpdatedImg
  }
  return rotaVisitTeamImg
}

export function FamilySettingsCustomisationPage() {
  const { t } = useTranslation(NAMESPACE.FAMILY_PORTAL_SETTINGS)
  const [form] = AntdForm.useForm<FamilySettingsFormValues>()
  const [isDirty, setIsDirty] = useState(false)
  const [isUpdateLoading, setIsUpdateLoading] = useState(false)
  const dispatch = useDispatch()
  const sendMixpanelEvent = useMixpanelEvent()

  const home = useSelector(
    (state: { global: { home: ExtendedHomeDto } }) => state.global.home
  )

  const initialValues: FamilySettingsFormValues = {
    familyDocuments: home.familyDocuments ?? false,
    familyCarePlans: home.familyCarePlans ?? false,
    familyMedications: home.familyMedications ?? false,
    familyRostering: home.familyRostering ?? false,
    familyShowDates: home.familyShowDates ?? false,
    familyShowNames: home.familyShowNames ?? false,
    familyLogCategoryItems: home.familyLogCategoryItems ?? [],
    familyRotaShowVisitInformation:
      home.familyRotaShowVisitInformation ?? false,
    familyRotaShowTeamMembers: home.familyRotaShowTeamMembers ?? false,
    familyRotaShowClockInOut: home.familyRotaShowClockInOut ?? false,
    familyRotaShowLastUpdatedBy: home.familyRotaShowLastUpdatedBy ?? false
  }

  const { mutateAsync: updateSettings } = useMutation(
    ['family-portal-settings', home.id],
    async (values: FamilySettingsFormValues) => {
      const res = await postJSON({
        url: `list/homes/${home.id}`,
        method: 'PUT',
        json: values
      })
      return res as { result: HomeDto }
    },
    {
      onSuccess: (res) => {
        dispatch(GlobalActions.setHomeDetails(res.result))
        dispatch(Operations.loadList('log-category-items'))
        showToast({ title: t(familyPortalSettingsConstants.SAVED) })
      }
    }
  )

  const handleOnFinish = async (values: FamilySettingsFormValues) => {
    try {
      setIsUpdateLoading(true)
      setIsDirty(false)
      const rosteringEnabled = values.familyRostering ?? false
      const teamMembersEnabled =
        rosteringEnabled && (values.familyRotaShowTeamMembers ?? false)
      const payload: FamilySettingsFormValues = {
        ...values,
        familyRotaShowVisitInformation: rosteringEnabled,
        familyRotaShowTeamMembers: rosteringEnabled
          ? values.familyRotaShowTeamMembers
          : false,
        familyRotaShowClockInOut: teamMembersEnabled
          ? values.familyRotaShowClockInOut
          : false,
        familyRotaShowLastUpdatedBy: rosteringEnabled
          ? values.familyRotaShowLastUpdatedBy
          : false
      }

      const originalRosteringState = {
        familyRostering: initialValues.familyRostering,
        familyRotaShowTeamMembers: initialValues.familyRotaShowTeamMembers,
        familyRotaShowClockInOut: initialValues.familyRotaShowClockInOut,
        familyRotaShowLastUpdatedBy: initialValues.familyRotaShowLastUpdatedBy
      }
      const nextRosteringState = {
        familyRostering: payload.familyRostering ?? false,
        familyRotaShowTeamMembers: payload.familyRotaShowTeamMembers ?? false,
        familyRotaShowClockInOut: payload.familyRotaShowClockInOut ?? false,
        familyRotaShowLastUpdatedBy:
          payload.familyRotaShowLastUpdatedBy ?? false
      }

      const hasRosteringSettingsChanged =
        originalRosteringState.familyRostering !==
          nextRosteringState.familyRostering ||
        originalRosteringState.familyRotaShowTeamMembers !==
          nextRosteringState.familyRotaShowTeamMembers ||
        originalRosteringState.familyRotaShowClockInOut !==
          nextRosteringState.familyRotaShowClockInOut ||
        originalRosteringState.familyRotaShowLastUpdatedBy !==
          nextRosteringState.familyRotaShowLastUpdatedBy

      if (hasRosteringSettingsChanged) {
        sendMixpanelEvent(MixpanelEvents.FAMILY_ROSTERING_SETTINGS_CHANGED, {
          familyRostering: nextRosteringState.familyRostering,
          showTeamMembers: nextRosteringState.familyRotaShowTeamMembers,
          showClockInOut: nextRosteringState.familyRotaShowClockInOut,
          showLastUpdatedBy: nextRosteringState.familyRotaShowLastUpdatedBy
        })
      }
      await updateSettings(payload)
    } catch (err: any) {
      setIsDirty(true)
      showToast({
        title: err?.message ?? t(familyPortalSettingsConstants.SAVE_FAILED),
        type: 'error'
      })
    } finally {
      setIsUpdateLoading(false)
    }
  }

  const handleFormValuesChange = (
    changedValues: Partial<FamilySettingsFormValues>
  ) => {
    if ('familyRostering' in changedValues) {
      form.setFieldValue(
        'familyRotaShowVisitInformation',
        changedValues.familyRostering ?? false
      )
    }
    if (
      'familyRotaShowTeamMembers' in changedValues &&
      !(changedValues.familyRotaShowTeamMembers ?? false)
    ) {
      form.setFieldValue('familyRotaShowClockInOut', false)
    }
    setIsDirty(true)
  }

  const showDates =
    AntdForm.useWatch('familyShowDates', form) ?? initialValues.familyShowDates
  const showNames =
    AntdForm.useWatch('familyShowNames', form) ?? initialValues.familyShowNames
  const appImgUrl = getLogDetailsImageUrl(showDates, showNames)
  const preferredCarePlansNominative =
    home.preferredCarePlansNominative ?? 'Care plans'
  const hasCarePlans =
    (home as HomeDto & { carePlans?: boolean }).carePlans ?? false
  const { hasRota } = useHomeType()
  const rosteringEnabled =
    AntdForm.useWatch('familyRostering', form) ?? initialValues.familyRostering
  const showRotaTeamMembers =
    AntdForm.useWatch('familyRotaShowTeamMembers', form) ??
    initialValues.familyRotaShowTeamMembers
  const showRotaVisitInformation =
    AntdForm.useWatch('familyRotaShowVisitInformation', form) ??
    rosteringEnabled
  const showRotaClockInOut =
    AntdForm.useWatch('familyRotaShowClockInOut', form) ??
    initialValues.familyRotaShowClockInOut
  const showRotaLastUpdatedBy =
    AntdForm.useWatch('familyRotaShowLastUpdatedBy', form) ??
    initialValues.familyRotaShowLastUpdatedBy
  const rotaPreviewImageUrl = getRotaDetailsImageUrl(
    showRotaVisitInformation,
    showRotaTeamMembers,
    showRotaClockInOut,
    showRotaLastUpdatedBy
  )

  return (
    <>
      <PageHeader
        title={t(familyPortalSettingsConstants.CUSTOMISATION_TITLE)}
        subtitle={t(familyPortalSettingsConstants.CUSTOMISATION_SUBTITLE)}
        moreInfoLink={HELP_ARTICLE_URL}
        sticky
      />
      <PageWrapper>
        <Form<FamilySettingsFormValues>
          form={form}
          onFinish={handleOnFinish}
          onValuesChange={handleFormValuesChange}
          initialValues={initialValues}
          layout="horizontal"
        >
          <FormCard
            title={t(familyPortalSettingsConstants.FEATURES_CARD_TITLE)}
          >
            <SwitchContainer>
              <AntdForm.Item
                name="familyDocuments"
                valuePropName="checked"
                noStyle
              >
                <Switch />
              </AntdForm.Item>
              <Text>{t(familyPortalSettingsConstants.DOCUMENTS)}</Text>
            </SwitchContainer>
            {hasCarePlans && (
              <>
                <SwitchContainer>
                  <AntdForm.Item
                    name="familyCarePlans"
                    valuePropName="checked"
                    noStyle
                  >
                    <Switch />
                  </AntdForm.Item>
                  <Text>
                    {t(
                      familyPortalSettingsConstants.CARE_PLANS_AND_RISK_ASSESSMENTS,
                      { carePlansNominative: preferredCarePlansNominative }
                    )}
                  </Text>
                </SwitchContainer>
                <SwitchContainer>
                  <AntdForm.Item
                    name="familyMedications"
                    valuePropName="checked"
                    noStyle
                  >
                    <Switch />
                  </AntdForm.Item>
                  <Text>{t(familyPortalSettingsConstants.MEDICATIONS)}</Text>
                </SwitchContainer>
              </>
            )}
            {hasRota && (
              <SwitchContainer>
                <AntdForm.Item
                  name="familyRostering"
                  valuePropName="checked"
                  noStyle
                >
                  <Switch />
                </AntdForm.Item>
                <Text>{t(familyPortalSettingsConstants.ROSTERING)}</Text>
              </SwitchContainer>
            )}
          </FormCard>

          {hasRota && rosteringEnabled && (
            <FormCard
              title={t(familyPortalSettingsConstants.ROTA_ON_FAMILY_APP)}
            >
              <RotaFamilyAppContent>
                <RotaFamilyAppSwitches>
                  <DescriptionText>
                    {t(
                      familyPortalSettingsConstants.ROTA_ON_FAMILY_APP_SUBTITLE
                    )}
                  </DescriptionText>
                  <SwitchContainer>
                    <AntdForm.Item
                      name="familyRotaShowVisitInformation"
                      valuePropName="checked"
                      noStyle
                    >
                      <SwitchWithMargin disabled />
                    </AntdForm.Item>
                    <LabelContent>
                      <VisitInfoSwitchRow>
                        <Text weight="medium">
                          {t(
                            familyPortalSettingsConstants.ROTA_VISIT_INFORMATION
                          )}
                        </Text>
                        <AlwaysShown>
                          <LockFilled />
                          <Text level={1.5}>
                            {t(familyPortalSettingsConstants.ROTA_ALWAYS_SHOWN)}
                          </Text>
                        </AlwaysShown>
                      </VisitInfoSwitchRow>
                      <DescriptionText>
                        {t(
                          familyPortalSettingsConstants.ROTA_VISIT_INFORMATION_DESCRIPTION
                        )}
                      </DescriptionText>
                    </LabelContent>
                  </SwitchContainer>

                  <SwitchContainer>
                    <AntdForm.Item
                      name="familyRotaShowTeamMembers"
                      valuePropName="checked"
                      noStyle
                    >
                      <SwitchWithMargin />
                    </AntdForm.Item>
                    <LabelContent>
                      <Text weight="medium">
                        {t(
                          familyPortalSettingsConstants.ROTA_TEAM_MEMBERS_ALLOCATED
                        )}
                      </Text>
                      <DescriptionText>
                        {t(
                          familyPortalSettingsConstants.ROTA_TEAM_MEMBERS_ALLOCATED_DESCRIPTION
                        )}
                      </DescriptionText>
                    </LabelContent>
                  </SwitchContainer>
                  <IndentedSwitchBlock>
                    <SwitchContainer>
                      <AntdForm.Item
                        name="familyRotaShowClockInOut"
                        valuePropName="checked"
                        noStyle
                      >
                        <SwitchWithMargin disabled={!showRotaTeamMembers} />
                      </AntdForm.Item>
                      <LabelContent>
                        <Text weight="medium">
                          {t(familyPortalSettingsConstants.ROTA_CLOCK_IN_OUT)}
                        </Text>
                        <DescriptionText>
                          {t(
                            familyPortalSettingsConstants.ROTA_CLOCK_IN_OUT_DESCRIPTION
                          )}
                        </DescriptionText>
                        {!showRotaTeamMembers && (
                          <WarningText>
                            {t(
                              familyPortalSettingsConstants.ROTA_REQUIRES_TEAM_MEMBERS
                            )}
                          </WarningText>
                        )}
                      </LabelContent>
                    </SwitchContainer>
                  </IndentedSwitchBlock>

                  <SwitchContainer>
                    <AntdForm.Item
                      name="familyRotaShowLastUpdatedBy"
                      valuePropName="checked"
                      noStyle
                    >
                      <SwitchWithMargin />
                    </AntdForm.Item>
                    <LabelContent>
                      <Text weight="medium">
                        {t(familyPortalSettingsConstants.ROTA_LAST_UPDATED_BY)}
                      </Text>
                      <DescriptionText>
                        {t(
                          familyPortalSettingsConstants.ROTA_LAST_UPDATED_BY_DESCRIPTION
                        )}
                      </DescriptionText>
                    </LabelContent>
                  </SwitchContainer>
                </RotaFamilyAppSwitches>

                <StyledImage
                  alt={t(familyPortalSettingsConstants.FAMILY_APP_PREVIEW)}
                  src={rotaPreviewImageUrl}
                />
              </RotaFamilyAppContent>
            </FormCard>
          )}

          <FormCard title={t(familyPortalSettingsConstants.LOG_DETAILS)}>
            <LogDetailsContent>
              <LogDetailsText>
                <SwitchContainer>
                  <AntdForm.Item
                    name="familyShowDates"
                    valuePropName="checked"
                    noStyle
                  >
                    <SwitchWithMargin />
                  </AntdForm.Item>
                  <LabelContent>
                    <Text weight="medium">
                      {t(familyPortalSettingsConstants.TIME_OF_LOG)}
                    </Text>
                    <DescriptionText>
                      {t(
                        familyPortalSettingsConstants.LOG_DETAILS_TIME_DESCRIPTION
                      )}
                    </DescriptionText>
                  </LabelContent>
                </SwitchContainer>
                <SwitchContainer style={{ marginTop: 24 }}>
                  <AntdForm.Item
                    name="familyShowNames"
                    valuePropName="checked"
                    noStyle
                  >
                    <SwitchWithMargin />
                  </AntdForm.Item>
                  <LabelContent>
                    <Text weight="medium">
                      {t(familyPortalSettingsConstants.LOGGED_BY)}
                    </Text>
                    <DescriptionText>
                      {t(
                        familyPortalSettingsConstants.LOG_DETAILS_LOGGED_BY_DESCRIPTION
                      )}
                    </DescriptionText>
                  </LabelContent>
                </SwitchContainer>
              </LogDetailsText>
              <StyledImage alt="app logs image" src={appImgUrl} />
            </LogDetailsContent>
          </FormCard>

          <FormCard
            title={t(familyPortalSettingsConstants.FAMILY_PORTAL_VISIBLE_LOGS)}
          >
            <DescriptionText>
              {t(familyPortalSettingsConstants.LOGS_SUBTITLE)}
            </DescriptionText>

            <DescriptionText>
              <strong>
                {t(familyPortalSettingsConstants.LOGS_NOTE_LABEL)}
              </strong>{' '}
              {t(familyPortalSettingsConstants.LOGS_NOTE)}
            </DescriptionText>

            <FormItem name="familyLogCategoryItems" noStyle>
              <FamilyLogCategoryItemsField />
            </FormItem>
          </FormCard>

          <AccessRestriction>
            <FixedFooter
              isUpdateLoading={isUpdateLoading}
              isDirty={isDirty}
              handleSubmit={() => handleSubmit(form, handleOnFinish)}
            />
          </AccessRestriction>
        </Form>
      </PageWrapper>

      <NavigationModal
        canNavigate={!isDirty}
        modalContentText={t(
          familyPortalSettingsConstants.NAVIGATION_MODAL_CONTENT
        )}
      />
    </>
  )
}

const PageWrapper = styled.div`
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 24px;
  max-width: 1080px;
  padding: 0 0 48px;
  margin-bottom: 48px;
`

const SwitchContainer = styled.div`
  display: flex;
  gap: 16px;
`

const DescriptionText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color4};
`
const LabelContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`
const SwitchWithMargin = styled(Switch)`
  margin-top: 4px;
`
const LogDetailsText = styled.div`
  flex: 1;
`
const StyledImage = styled.img`
  width: 280px;
`
const LogDetailsContent = styled.div`
  display: flex;
  align-items: flex-start;
  padding-top: 8px;
  gap: 40px;
  flex-wrap: wrap;
`

const RotaFamilyAppContent = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 40px;
`

const RotaFamilyAppSwitches = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const VisitInfoSwitchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const IndentedSwitchBlock = styled.div`
  margin-top: 16px;
  margin-left: 24px;
  padding-left: 24px;
  border-left: 2px solid ${({ theme }) => theme.color.neutral.color8};
`

const AlwaysShown = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  color: ${({ theme }) => theme.color.neutral.color5};
  font-size: 12px;
`

const WarningText = styled(Text)`
  color: ${({ theme }) => theme.color.status.warning};
`
